import { Button, Input } from '@newfront-insurance/core-ui/v2';
import { useState } from 'react';

import { ALL_ACCOUNTS_SCHEDULE_LABEL, toNovaScheduleRecurrenceInput } from './nova-schedule-model';
import type { FileAttachment } from '../../../../hooks/use-file-selection';
import {
  type NovaScheduleProposalDraft,
  validateNovaScheduleProposalDraft,
} from '../../../../lib/nova-schedule-proposal';
import type { UpdateNovaScheduleCommand, NovaScheduleRecord } from '../../../../queries/nova-schedule-management';
import {
  NovaPromptEditor,
  buildNovaPromptFiles,
  hasUnsettledNovaPromptAttachments,
  mergeNovaPromptFiles,
  type NovaPromptFile,
} from '../chat-input/nova-prompt-editor';
import { NovaScheduleDeliveryEditor, NovaScheduleWhenEditor } from '../tools/nova-schedule-proposal-fields';

export function NovaScheduleEditor({
  schedule,
  isSaving,
  error,
  onCancel,
  onSave,
}: {
  schedule: NovaScheduleRecord;
  isSaving: boolean;
  error?: unknown;
  onCancel: () => void;
  onSave: (command: UpdateNovaScheduleCommand) => Promise<void>;
}) {
  const [draft, setDraft] = useState<NovaScheduleProposalDraft>(() => ({
    title: schedule.title,
    prompt: schedule.prompt,
    timezone: schedule.timezone,
    startAt: schedule.startAt,
    recurrence: toNovaScheduleRecurrenceInput(schedule.recurrence),
    outputPolicy: schedule.outputPolicy,
  }));
  const [promptFiles, setPromptFiles] = useState<NovaPromptFile[]>(() =>
    buildNovaPromptFiles(
      schedule.attachmentFileUuids,
      schedule.attachmentFiles.map((file) => ({ uuid: file.uuid, filename: file.filename })),
    ),
  );
  const [promptAttachments, setPromptAttachments] = useState<FileAttachment[]>([]);
  const mergedPromptFiles = mergeNovaPromptFiles(promptFiles, promptAttachments);
  const attachmentFileUuids = mergedPromptFiles.map((file) => file.uuid);
  const hasUnsettledAttachments = hasUnsettledNovaPromptAttachments(promptAttachments);
  const validationError = validateNovaScheduleProposalDraft(draft);
  const isDirty =
    draft.title.trim() !== schedule.title ||
    draft.prompt.trim() !== schedule.prompt ||
    draft.timezone !== schedule.timezone ||
    draft.startAt !== schedule.startAt ||
    JSON.stringify(draft.recurrence) !== JSON.stringify(toNovaScheduleRecurrenceInput(schedule.recurrence)) ||
    draft.outputPolicy !== schedule.outputPolicy ||
    JSON.stringify(attachmentFileUuids) !== JSON.stringify(schedule.attachmentFileUuids ?? []);

  const save = async () => {
    if (validationError || !isDirty || isSaving || hasUnsettledAttachments) return;

    await onSave({
      title: draft.title.trim(),
      prompt: draft.prompt.trim(),
      attachmentFileUuids,
      timezone: draft.timezone,
      recurrence: toNovaScheduleRecurrenceInput(draft.recurrence),
      ...(draft.outputPolicy ? { outputPolicy: draft.outputPolicy } : {}),
      ...(draft.startAt ? { startAt: draft.startAt } : {}),
    });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white">
      <div className="min-h-0 flex-1 overflow-y-auto px-8 pt-10 pb-8 [scrollbar-gutter:stable_both-edges]">
        <div className="mx-auto flex w-[736px] max-w-full flex-col gap-6">
          <h2 className="text-steel-400 text-[17px] leading-6">Edit schedule</h2>

          <EditorField label="Account" htmlFor="nova-schedule-edit-account">
            <Input
              fieldId="nova-schedule-edit-account"
              value={schedule.account?.name ?? ALL_ACCOUNTS_SCHEDULE_LABEL}
              disabled
            />
          </EditorField>

          <EditorField label="Briefing name*" htmlFor="nova-schedule-edit-title">
            <Input
              fieldId="nova-schedule-edit-title"
              value={draft.title}
              maxLength={200}
              disabled={isSaving}
              onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
            />
          </EditorField>

          <div className="flex flex-col gap-2">
            <span className="text-steel-500 text-[14px] leading-5 font-semibold">What to include*</span>
            <NovaPromptEditor
              value={draft.prompt}
              accountUuid={schedule.accountUuid ?? undefined}
              existingFiles={promptFiles}
              attachments={promptAttachments}
              disabled={isSaving}
              onChange={(prompt) => setDraft((current) => ({ ...current, prompt }))}
              onExistingFilesChange={setPromptFiles}
              setAttachments={setPromptAttachments}
            />
          </div>

          {draft.recurrence.preset !== 'ONCE' ? (
            <div className="flex flex-col gap-2">
              <span className="text-steel-500 text-[14px] leading-5 font-semibold">Delivery</span>
              <div className="border-steel-75 rounded-[4px] border p-3">
                <NovaScheduleDeliveryEditor
                  value={draft.outputPolicy}
                  disabled={isSaving}
                  onChange={(outputPolicy) => setDraft((current) => ({ ...current, outputPolicy }))}
                />
              </div>
            </div>
          ) : null}

          <div className="flex flex-col gap-2">
            <span className="text-steel-500 text-[14px] leading-5 font-semibold">Schedule*</span>
            <div className="border-steel-75 bg-steel-50 rounded-[4px] border p-6">
              <NovaScheduleWhenEditor draft={draft} timezone={draft.timezone} disabled={isSaving} onChange={setDraft} />
            </div>
          </div>

          {validationError ? <p className="text-[13px] leading-5 text-[#C5342A]">{validationError}</p> : null}
          {error ? (
            <p className="text-[13px] leading-5 text-[#C5342A]">Unable to save this schedule. Try again.</p>
          ) : null}
        </div>
      </div>

      <div className="border-steel-75 shrink-0 border-t bg-white px-8 py-4">
        <div className="mx-auto flex w-[736px] max-w-full gap-2.5">
          <Button type="button" variant="secondary" className="flex-1" disabled={isSaving} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            className="flex-1"
            disabled={Boolean(validationError) || !isDirty || isSaving || hasUnsettledAttachments}
            onClick={() => void save()}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function EditorField({ label, htmlFor, children }: { label: string; htmlFor: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={htmlFor} className="text-steel-500 text-[14px] leading-5 font-semibold">
        {label}
      </label>
      {children}
    </div>
  );
}
